import React, { useEffect, useState } from 'react';
import {
  HbrIcon,
  HbrOption,
  HbrSelect,
  HbrSpinner,
  HbrTextarea,
} from '@harbor/elements-react-standalone';
import { useParams } from 'react-router-dom';
import StbButton from '../security-toolbox-ui/StbButton';
import { statusWarning } from '@harbor/elements/icons';
import { useFetchOpportunityProducts } from '../../hooks/opportunity-hooks';
import { postItem } from '../../support/queries';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { asSingleValue } from '../../support/utils';


// Map sf_technology value to picklist controller index
const TECH_TO_PICKLIST_INDEX: Record<string, number> = {
  'Identity & Access Management': 5,
  'Network Security': 6,
  'Secure Service Edge': 7,
  'Threat Intelligence, Detection & Response': 8,
};

// All picklist values as described in the prompt
const BLOCKER_PICKLIST_VALUES = [
  // Identity & Access Management (5)
  { label: 'Duo-Advanced MFA beyond just web based authentication', value: 'Duo-Advanced MFA beyond just web based authentication', validFor: [5] },
  { label: 'Duo-Differentiated Cisco IdP', value: 'Duo-Differentiated Cisco IdP', validFor: [5] },
  { label: 'Duo-FedRAMP High and GCC High Support', value: 'Duo-FedRAMP High and GCC High Support', validFor: [5] },
  { label: 'Duo-Identity Intelligence-as-a-Service', value: 'Duo-Identity Intelligence-as-a-Service', validFor: [5] },
  { label: 'Duo-Improved user/desktop biometric authentication', value: 'Duo-Improved user/desktop biometric authentication', validFor: [5] },
  { label: 'Duo-Other', value: 'Duo-Other', validFor: [5] },
  { label: 'ISE-Classification', value: 'ISE-Classification\u200b', validFor: [5] },
  { label: 'ISE-Common Policy', value: 'ISE-Common Policy\u200b', validFor: [5] },
  { label: 'ISE-Core/Operations', value: 'ISE-Core/Operations\u200b', validFor: [5] },
  { label: 'ISE-Deployment', value: 'ISE-Deployment\u200b', validFor: [5] },
  { label: 'ISE-Enforcement', value: 'ISE-Enforcement\u200b', validFor: [5] },
  { label: 'ISE-Other', value: 'ISE-Other', validFor: [5] },
  { label: 'Identity & Access Management-Other', value: 'Identity & Access Management-Other', validFor: [5] },

  // Network Security (6)
  { label: 'Firewall-Cloud/Virtual Feature', value: 'Firewall-Cloud/Virtual Feature', validFor: [6] },
  { label: 'Firewall-Core Firewall Feature', value: 'Firewall-Core Firewall Feature', validFor: [6] },
  { label: 'Firewall-Identity Feature', value: 'Firewall-Identity Feature', validFor: [6] },
  { label: 'Firewall-Integration Feature', value: 'Firewall-Integration Feature', validFor: [6] },
  { label: 'Firewall-Other', value: 'Firewall-Other', validFor: [6] },
  { label: 'Network Security-Other', value: 'Network Security-Other', validFor: [6] },

  // Secure Service Edge (7)
  { label: 'Secure Access-CASB / DLP', value: 'Secure Access-CASB / DLP', validFor: [7] },
  { label: 'Secure Access-Identity / Posture enhancements', value: 'Secure Access-Identity / Posture enhancements\u200b', validFor: [7] },
  { label: 'Secure Access-Integrations and Tunnel Support', value: 'Secure Access-Integrations and Tunnel Support', validFor: [7] },
  { label: 'Secure Access-Secure Private Access Enhancements', value: 'Secure Access-Secure Private Access Enhancements', validFor: [7] },
  { label: 'Secure Access-Umbrella Migration to SSE', value: 'Secure Access-Umbrella Migration to SSE', validFor: [7] },
  { label: 'Secure Access-Other', value: 'Secure Access-Other', validFor: [7] },
  { label: 'Secure Service Edge-Other', value: 'Secure Service Edge-Other', validFor: [7] },

  // Threat Intelligence, Detection & Response (8)
  { label: 'XDR-Cisco Integrations', value: 'XDR-Cisco Integrations', validFor: [8] },
  { label: 'XDR-Detections & Correlations', value: 'XDR-Detections & Correlations', validFor: [8] },
  { label: 'XDR-General Usability', value: 'XDR-General Usability', validFor: [8] },
  { label: 'XDR-Third Party integrations', value: 'XDR-Third Party integrations', validFor: [8] },
  { label: 'XDR-Other', value: 'XDR-Other', validFor: [8] },
  { label: 'Threat Intelligence Detections & Response-Other', value: 'Threat Intelligence Detections & Response-Other', validFor: [8] },
];

const CURRENCY = 'USD';
const BUSINESS_ENTITY = 'Security';

const TechnicalBlockerLogger: React.FC = () => {
  const { id: opportunityId } = useParams();
  const { data: products, isLoading: productsLoading } = useFetchOpportunityProducts(opportunityId);

  const [open, setOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [techArea, setTechArea] = useState<string>('');
  const [blockerArea, setBlockerArea] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [commentsTouched, setCommentsTouched] = useState<boolean>(false);

  // Derive products with a valid sf_technology
  const validProducts = (products ?? []).filter(
    (p: any) => !!p.sf_technology && TECH_TO_PICKLIST_INDEX[p.sf_technology]
  );

  // When product changes, set tech area accordingly
  useEffect(() => {
    if (selectedProduct) {
      setTechArea(selectedProduct.sf_technology);
      setBlockerArea('');
    }
  }, [selectedProduct]);

  // Get the controller index for the selected technology area
  const picklistIndex = techArea ? TECH_TO_PICKLIST_INDEX[techArea] : undefined;

  // Filter picklist values for the technology area
  const blockerOptions = BLOCKER_PICKLIST_VALUES.filter(
    (v) => picklistIndex && v.validFor.includes(picklistIndex)
  );

  // Find out if current blockerArea is "Other" (for comment requirement)
  const isOtherBlocker =
    blockerArea && blockerArea.toLowerCase().includes('other');

  // Validation
  const canSubmit =
    selectedProduct &&
    techArea &&
    blockerArea &&
    (!isOtherBlocker || comments.trim().length > 0);

  const handleOpen = () => {
    setOpen(true);
    setSelectedProduct(null);
    setTechArea('');
    setBlockerArea('');
    setComments('');
    setCommentsTouched(false);
  };

  const handleClose = async () => {
    setOpen(false);
    setSelectedProduct(null);
    setTechArea('');
    setBlockerArea('');
    setComments('');
    setCommentsTouched(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const payload = {
      CurrencyIsoCode: CURRENCY,
      Business_Entity_Type__c: BUSINESS_ENTITY,
      Comments__c: comments,
      Opportunity__c: opportunityId,
      Blocker_Area__c: blockerArea,
      Technology_Area__c: techArea,
      Feature_ID__c: null,
      Feature_ID_URL__c: null,
    };

    await postItem('sf/sobject/Technical_Blocker__c', payload);
  };

  return (
    <div>
      <StbButton onClick={handleOpen} className="hbr-button-group__button hbr-button-group__button--last">Log Technical Blocker</StbButton>
      <StbApiModal
        title="Log Technical Blocker"
        open={open}
        role="save"
        submitLabel="Save Blocker"
        submitDisabled={!canSubmit}
        submitSuccess="Technical Blocker logged successfully"
        submitError="Failed to log Technical Blocker"
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        {productsLoading && <HbrSpinner size="medium" />}
        {!productsLoading && validProducts.length === 0 && (
          <div
            className="p-3 rounded-md mb-4 flex items-start gap-3 text-sm"
            style={{ border: '1px solid var(--hbr-color-warning-35)' }}
          >
            <HbrIcon size="24px" asset={statusWarning} sentiment={'warning'} />
            No associated products have an available technical blocker.
          </div>
        )}
        {!productsLoading && validProducts.length > 0 && (
          <>
            <HbrSelect
              label="Product"
              placeholder="Select Product"
              value={selectedProduct ? String(selectedProduct.id) : ''}
              onHbr-change={(evt) => {
                const prod = validProducts.find((p: any) => String(p.id) === evt.target.value);
                setSelectedProduct(prod);
              }}
            >
              {validProducts.map((p: any) => (
                <HbrOption value={String(p.id)} key={String(p.id)}>
                  {p.name} ({p.sf_technology})
                </HbrOption>
              ))}
              <div slot="label">Product <em className="text-gray-500">(required)</em></div>
            </HbrSelect>
            {techArea && (
              <HbrSelect
                label="Blocker Area"
                placeholder="Select Blocker"
                value={blockerArea}
                onHbr-change={(evt) => setBlockerArea(asSingleValue(evt.target.value))}
              >
                {blockerOptions.map((o) => (
                  <HbrOption value={o.value} key={o.value}>
                    {o.label}
                  </HbrOption>
                ))}
              </HbrSelect>
            )}
            {techArea && (
              <HbrTextarea
                value={comments}
                onHbr-input={(evt) => {
                  const value = evt.target.value;
                  setCommentsTouched(true);
                  setComments(value);
                }}
                required={isOtherBlocker}
                error={!!isOtherBlocker && commentsTouched && comments?.trim() === ''}
              >
                <div slot="label">Comments {isOtherBlocker && <em className="text-gray-500">(required)</em>}</div>
                {!!isOtherBlocker && commentsTouched && comments?.trim() === '' && (
                  <div slot="help-text" className="text-sm mt-2 text-danger">
                    Comment is required for &quot;Other&quot; blocker selection
                  </div>
                )}
              </HbrTextarea>
            )}
          </>
        )}
      </StbApiModal>
    </div>
  );
};

export default TechnicalBlockerLogger;